import { Injectable } from '@angular/core';
import { ClinicDataService } from './data-service/clinic-data-service';
import { UserCache } from './user-cache';

export const PATIENT_CARE_FUNCTION_CODE = 'PAT_PatientCare';
export const PATIENT_CARE_ROUTE = 'registers/patient-care';
export const POLARIS_THEME_MESSAGE_TYPE = 'chis-theme';
export const POLARIS_THEME_MESSAGE_VERSION = 1;
export const POLARIS_CUSNO_FOCUS_MESSAGE_TYPE = 'polaris-cusno-focus';
export const CHIS_PATIENT_NO_MESSAGE_TYPE = 'chis-patient-no';

export interface PolarisThemeMessage {
  type: typeof POLARIS_THEME_MESSAGE_TYPE;
  version: typeof POLARIS_THEME_MESSAGE_VERSION;
  nonce: string;
  theme: {
    fontColor: string;
  };
}

export interface PolarisCusnoFocusMessage {
  type: typeof POLARIS_CUSNO_FOCUS_MESSAGE_TYPE;
  version: typeof POLARIS_THEME_MESSAGE_VERSION;
  nonce: string;
  field: 'cusno';
}

export interface ChisPatientNoMessage {
  type: typeof CHIS_PATIENT_NO_MESSAGE_TYPE;
  version: typeof POLARIS_THEME_MESSAGE_VERSION;
  nonce: string;
  patientNo: string;
}

/**
 * Provides the current Polaris host URL and caches the browser-level reachability
 * probe for the current login session.
 */
@Injectable({
  providedIn: 'root'
})
export class PolarisHostService {
  private readonly probeTimeoutMs = 3000;
  private probeKey = '';
  private probePromise: Promise<boolean> = null;

  constructor(private clinicDataService: ClinicDataService) { }

  public getHostUrl(): string {
    return this.clinicDataService.GetPolarisHostUrl();
  }

  public getCustomerQueryUrl(themeNonce?: string): string {
    const hostUrl = this.getHostUrl();
    if (!hostUrl) {
      return '';
    }

    const url = new URL(hostUrl);
    url.pathname = this.appendPath(url.pathname, 'ecrm/customerQuery.php');
    url.searchParams.set('source', 'chis');
    if (themeNonce) {
      url.searchParams.set('themeNonce', themeNonce);
      url.searchParams.set('parentOrigin', window.location.origin);
    }
    url.hash = '';
    return url.toString();
  }

  public createThemeNonce(): string {
    return this.createNonce();
  }

  public sendThemeMessage(iframe: HTMLIFrameElement, nonce: string, fontColor: string): boolean {
    const color = (fontColor || '').trim();
    const hostUrl = this.getHostUrl();
    if (!iframe?.contentWindow || !nonce || !hostUrl || !this.isCssColor(color)) {
      return false;
    }

    const targetOrigin = new URL(hostUrl).origin;
    const message: PolarisThemeMessage = {
      type: POLARIS_THEME_MESSAGE_TYPE,
      version: POLARIS_THEME_MESSAGE_VERSION,
      nonce,
      theme: { fontColor: color }
    };
    iframe.contentWindow.postMessage(message, targetOrigin);
    return true;
  }

  public isTrustedCusnoFocusMessage(
    event: MessageEvent,
    iframe: HTMLIFrameElement,
    nonce: string
  ): boolean {
    const targetOrigin = this.getHostOrigin();
    const data = event?.data as Partial<PolarisCusnoFocusMessage>;
    return !!targetOrigin
      && !!iframe?.contentWindow
      && !!nonce
      && event?.origin === targetOrigin
      && event?.source === iframe.contentWindow
      && data?.type === POLARIS_CUSNO_FOCUS_MESSAGE_TYPE
      && data?.version === POLARIS_THEME_MESSAGE_VERSION
      && data?.nonce === nonce
      && data?.field === 'cusno';
  }

  public sendPatientNoMessage(iframe: HTMLIFrameElement, nonce: string, patientNo: string): boolean {
    const normalizedPatientNo = String(patientNo || '').trim();
    const targetOrigin = this.getHostOrigin();
    if (!iframe?.contentWindow || !nonce || !normalizedPatientNo || !targetOrigin) {
      return false;
    }

    const message: ChisPatientNoMessage = {
      type: CHIS_PATIENT_NO_MESSAGE_TYPE,
      version: POLARIS_THEME_MESSAGE_VERSION,
      nonce,
      patientNo: normalizedPatientNo
    };
    iframe.contentWindow.postMessage(message, targetOrigin);
    return true;
  }

  public probeOnce(): Promise<boolean> {
    const hostUrl = this.getHostUrl();
    if (!hostUrl) {
      return Promise.resolve(false);
    }

    const loginUser = UserCache.getLoginUser();
    const loginKey = loginUser?.AuthToken || `${loginUser?.Clinic?.Id || ''}:${loginUser?.UserId || ''}`;
    const key = `${loginKey}|${hostUrl}`;
    if (this.probeKey !== key) {
      this.probeKey = key;
      this.probePromise = null;
    }

    if (!this.probePromise) {
      this.probePromise = this.probe(hostUrl);
    }
    return this.probePromise;
  }

  private probe(hostUrl: string): Promise<boolean> {
    const host = new URL(hostUrl);
    const origin = host.origin;
    const nonce = this.createNonce();
    const parentOrigin = window.location.origin;
    const probeUrl = new URL(hostUrl);
    probeUrl.pathname = this.appendPath(probeUrl.pathname, 'webapi/chis/ping.php');
    probeUrl.searchParams.set('parentOrigin', parentOrigin);
    probeUrl.searchParams.set('nonce', nonce);
    probeUrl.hash = '';

    return new Promise<boolean>(resolve => {
      const iframe = document.createElement('iframe');
      let timeoutId: number;
      let settled = false;

      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        window.clearTimeout(timeoutId);
        iframe.remove();
      };

      const finish = (result: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(result);
      };

      const onMessage = (event: MessageEvent) => {
        const data = event.data;
        if (event.origin !== origin || event.source !== iframe.contentWindow) {
          return;
        }
        if (!data || data.type !== 'polaris-ping' || data.nonce !== nonce) {
          return;
        }
        finish(true);
      };

      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.display = 'none';
      window.addEventListener('message', onMessage);
      timeoutId = window.setTimeout(() => finish(false), this.probeTimeoutMs);
      document.body.appendChild(iframe);
      iframe.src = probeUrl.toString();
    });
  }

  private appendPath(pathname: string, path: string): string {
    const basePath = (pathname || '').replace(/\/+$/, '');
    return `${basePath}/${path}`;
  }

  private getHostOrigin(): string {
    const hostUrl = this.getHostUrl();
    if (!hostUrl) {
      return '';
    }

    try {
      return new URL(hostUrl).origin;
    } catch (ex) {
      return '';
    }
  }

  private isCssColor(value: string): boolean {
    if (!value) {
      return false;
    }
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      return CSS.supports('color', value);
    }

    const probe = document.createElement('span');
    probe.style.color = '';
    probe.style.color = value;
    return probe.style.color !== '';
  }

  private createNonce(): string {
    const bytes = new Uint8Array(16);
    if (window.crypto?.getRandomValues) {
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
    }
    return `${Date.now()}-${Math.random().toString(36).substring(2)}`;
  }
}
